// src/components/auth/AuthCallback.jsx
// Landing page on the way back from the DDD Auth Hub. Signs in with the hub
// and spoke tokens, then goes on to where the person was heading. Ported from
// The Art of Conversation (after The Unspoken Dialogue), including the hub's
// "continue as guest" return, which carries no tokens and goes on signed out.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHubService } from '../../services/authHub';

const AuthCallback = () => {
  const navigate = useNavigate();
  const ran = useRef(false); // StrictMode runs effects twice; exchange once.
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Read the destination BEFORE handleTokenExchange cleans the URL.
    const redirectPath = authHubService.getRedirectPath();

    if (authHubService.isGuestReturn()) {
      authHubService.cleanUrl();
      navigate(redirectPath, { replace: true });
      return;
    }

    (async () => {
      try {
        await authHubService.handleTokenExchange();
        navigate(redirectPath, { replace: true });
      } catch (err) {
        console.error('[AuthCallback] Token exchange failed:', err);
        // The tokens are useless now; don't leave them in the address bar.
        authHubService.cleanUrl();
        setError(err?.message || 'Sign-in could not be completed.');
      }
    })();
  }, [navigate]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-950 px-6">
      <div className="text-center max-w-md">
        <img
          src="/art-of-story-logo.png"
          alt="The Art of Story"
          className="w-24 h-24 mx-auto mb-5 rounded-2xl"
        />
        {error ? (
          <>
            <h1 className="text-xl font-semibold text-white mb-2">We couldn’t finish signing you in</h1>
            <p className="text-slate-300 text-sm mb-6">{error}</p>
            <button
              type="button"
              onClick={() => authHubService.redirectToLogin('/')}
              className="min-h-[44px] px-5 py-2 rounded-lg bg-amber-700 hover:bg-amber-800 text-white font-semibold transition-colors"
            >
              Try again
            </button>
          </>
        ) : (
          <p className="text-slate-200" role="status">Signing you in…</p>
        )}
      </div>
    </main>
  );
};

export default AuthCallback;
